import { useEffect, useMemo, useState, useCallback } from 'react'
import {
  ArrowBackIcon,
  Card,
  CardBody,
  CardHeader,
  Flex,
  IconButton,
  Button,
  Text,
  BalanceInput,
  Slider,
  Box,
  AutoRenewIcon,
} from 'uikit'
import styled from 'styled-components'
import { BigNumber, FixedNumber } from '@ethersproject/bignumber'
import { Zero } from '@ethersproject/constants'
import { parseUnits } from '@ethersproject/units'
import { useWeb3React } from '@web3-react/core'
import { useGetMinBetAmount } from 'state/predictions/hooks'
import { useTranslation } from 'react-i18next'
import { usePredictionsContract } from 'hooks/useContract'
import { useGetMjtBalance } from 'hooks/useTokenBalance'
import { useCallWithGasPrice } from 'hooks/useCallWithGasPrice'
import useCatchTxError from 'hooks/useCatchTxError'
import { BetPosition } from 'state/types'
import { formatBigNumber, formatFixedNumber } from 'utils/formatBalance'
import ConnectWalletButton from 'components/ConnectWalletButton'
import { useConfig } from 'pages/Predictions/context/ConfigProvider'
import useMjtApprovalStatus from 'hooks/useMJTApprovalStatus'
import useMjtApprove from 'hooks/useMjtApprove'
import { getTokenUrl } from 'utils/getTokenUrl'
import { Image } from 'components'
import PositionTag from '../PositionTag'

// const LOGOS = {
//   BTCK: AutoRenewIcon,
//   ETH: AutoRenewIcon,
// }

interface SetPositionCardProps {
  position: BetPosition
  togglePosition: () => void
  epoch: number
  onBack: () => void
  onSuccess: (hash: string) => Promise<void>
}

const dust = parseUnits('0.00', 18)
const percentShortcuts = [10, 25, 50, 75]

const getButtonProps = (value: BigNumber, mjtBalance: BigNumber, minBetAmountBalance: BigNumber) => {
  const hasSufficientBalance = () => {
    if (value.gt(0)) {
      return value.lte(mjtBalance)
    }
    return mjtBalance.gt(0)
  }

  if (!hasSufficientBalance()) {
    return { key: 'Insufficient MJT balance', disabled: true }
  }

  if (value.eq(0)) {
    return { key: 'Enter an amount', disabled: true }
  }

  return { key: 'Confirm', disabled: value.lt(minBetAmountBalance) }
}

const getValueAsEthersBn = (value: string) => {
  const valueAsFloat = parseFloat(value)
  return Number.isNaN(valueAsFloat) ? Zero : parseUnits(value)
}

const ArrowTag = styled(Flex)`
  align-items: center;
  background: white;
  border-radius: 16px;
  height: 24px;
`

const SetPositionCard: React.FC<React.PropsWithChildren<SetPositionCardProps>> = ({
  position,
  togglePosition,
  epoch,
  onBack,
  onSuccess,
}) => {
  const [value, setValue] = useState('')
  const [errorMessage, setErrorMessage] = useState(null)
  const [percent, setPercent] = useState(0)

  const { account } = useWeb3React()
  const minBetAmount = useGetMinBetAmount()
  const { t } = useTranslation()
  const { fetchWithCatchTxError, loading: isTxPending } = useCatchTxError()
  const { callWithGasPrice } = useCallWithGasPrice()
  const { address: predictionsAddress, token, payToken } = useConfig()
  const predictionsContract = usePredictionsContract(predictionsAddress, token.symbol)

  const { isVaultApproved, setLastUpdated } = useMjtApprovalStatus(predictionsAddress)
  const { handleApprove, pendingTx } = useMjtApprove(setLastUpdated, predictionsAddress, t('PREDICTION_44'))

  // KCS prediction doesn't need approval
  const doesMjtApprovePrediction = token.symbol === 'KCS' || isVaultApproved

  const { balance: mjtBalance } = useGetMjtBalance()

  const maxBalance = useMemo(() => {
    return mjtBalance.gt(dust) ? mjtBalance.sub(dust) : Zero
  }, [mjtBalance])
  const balanceDisplay = formatBigNumber(mjtBalance, 4)
  const valueAsBn = getValueAsEthersBn(value)
  const showFieldWarning = account && valueAsBn.gt(0) && errorMessage !== null

  const handleInputChange = (input: string) => {
    const inputAsBn = getValueAsEthersBn(input)

    if (inputAsBn.eq(0)) {
      setPercent(0)
    } else {
      const inputAsFn = FixedNumber.from(inputAsBn)
      const maxValueAsFn = FixedNumber.from(maxBalance)
      const hundredAsFn = FixedNumber.from(100)
      const percentage = inputAsFn.divUnsafe(maxValueAsFn).mulUnsafe(hundredAsFn)
      const percentageAsFloat = percentage.toUnsafeFloat()

      setPercent(percentageAsFloat > 100 ? 100 : percentageAsFloat)
    }
    setValue(input)
  }

  const handlePercentChange = useCallback(
    (sliderPercent: number) => {
      if (sliderPercent > 0) {
        const maxValueAsFn = FixedNumber.from(maxBalance)
        const hundredAsFn = FixedNumber.from(100)
        const sliderPercentAsFn = FixedNumber.from(sliderPercent.toFixed(18)).divUnsafe(hundredAsFn)
        const balancePercentage = maxValueAsFn.mulUnsafe(sliderPercentAsFn)
        setValue(formatFixedNumber(balancePercentage))
      } else {
        setValue('')
      }
      setPercent(sliderPercent)
    },
    [maxBalance]
  )

  // Clear value
  const handleGoBack = () => {
    setValue('')
    setPercent(0)
    onBack()
  }

  const { key, disabled } = getButtonProps(valueAsBn, maxBalance, minBetAmount)

  const handleEnterPosition = async () => {
    const betMethod = position === BetPosition.BULL ? 'betBull' : 'betBear'
    const callOptions = {
      gasLimit: 300000,
      value: 0,
    }

    const args = [epoch, valueAsBn.toString()]

    const receipt = await fetchWithCatchTxError(() => {
      return callWithGasPrice(predictionsContract, betMethod, args, callOptions)
    })
    if (receipt?.status) {
      onSuccess(receipt.transactionHash)
    }
  }

  // Warnings
  useEffect(() => {
    const inputAmount = getValueAsEthersBn(value)
    const hasSufficientBalance = inputAmount.gt(0) && inputAmount.lte(maxBalance)

    if (!hasSufficientBalance) {
      setErrorMessage(t('PREDICTION_45'))
    } else if (inputAmount.gt(0) && inputAmount.lt(minBetAmount)) {
      setErrorMessage(t('PREDICTION_52', { amount: formatBigNumber(minBetAmount) }))
    } else {
      setErrorMessage(null)
    }
  }, [value, maxBalance, minBetAmount, setErrorMessage, t, token.symbol])

  return (
    <Card>
      <CardHeader p="4px 12px" style={{ background: 'linear-gradient(91.72deg, #14D89D 1.03%, #0AC4DD 102.03%)' }}>
        <Flex alignItems="center" justifyContent="space-between">
          <Flex alignItems="center">
            <IconButton variant="text" scale="sm" onClick={handleGoBack}>
              <ArrowBackIcon width="16px" color="white" />
            </IconButton>
            <Text color="white" fontSize="14px" bold>
              {t('PREDICTION_46')}
            </Text>
          </Flex>
          <ArrowTag>
            <PositionTag betPosition={position} onClick={togglePosition}>
              {position === BetPosition.BULL ? t('Up') : t('Down')}
            </PositionTag>
          </ArrowTag>
        </Flex>
      </CardHeader>
      <CardBody py="16px">
        <Flex alignItems="center" justifyContent="space-between" mb="8px">
          <Text textAlign="right" color="textRemark" fontSize="14px" bold>
            {t('PREDICTION_47')}:
          </Text>
          <Flex alignItems="center">
            <Image src={getTokenUrl('MJT')} style={{ width: '20px', height: '20px', borderRadius: '20px' }} />
            <Text bold textTransform="uppercase" fontSize="14px" ml="4px">
              MJT
            </Text>
          </Flex>
        </Flex>
        <BalanceInput
          value={value}
          onUserInput={handleInputChange}
          isWarning={showFieldWarning}
          inputProps={{ disabled: !account || isTxPending }}
          className={!account || isTxPending ? '' : 'swiper-no-swiping'}
          style={{ height: '48px', borderColor: 'rgba(115, 126, 141, 0.16)', padding: '4px 16px' }}
        />
        {showFieldWarning && (
          <Text color="failure" fontSize="12px" mt="4px" textAlign="right">
            {errorMessage}
          </Text>
        )}
        <Text textAlign="right" mb="5px" color="textRemark" fontSize="14px" mt="3px" style={{ height: '18px' }}>
          {/* {account && t('Balance: %balance%', { balance: balanceDisplay })} */}
          {account && t('PREDICTION_48', { amount: balanceDisplay })}
        </Text>
        <Slider
          name="balance"
          min={0}
          max={100}
          value={percent}
          onValueChanged={handlePercentChange}
          valueLabel={account ? `${percent.toFixed(percent > 0 ? 1 : 0)}%` : ''}
          step={0.01}
          disabled={!account || isTxPending}
          mb="4px"
          className={!account || isTxPending ? '' : 'swiper-no-swiping'}
        />
        <Flex alignItems="center" justifyContent="space-between" mb="16px">
          {percentShortcuts.map((percentShortcut) => {
            const handleClick = () => {
              handlePercentChange(percentShortcut)
            }

            return (
              <Button
                key={percentShortcut}
                scale="xs"
                variant="tertiary"
                onClick={handleClick}
                disabled={!account || isTxPending}
                className={!account || isTxPending ? '' : 'slider-button'}
              >
                {`${percentShortcut}%`}
              </Button>
            )
          })}
          <Button
            scale="xs"
            variant="tertiary"
            onClick={() => handlePercentChange(100)}
            disabled={!account || isTxPending}
            className={!account || isTxPending ? '' : 'slider-button'}
          >
            {t('PREDICTION_49')}
          </Button>
        </Flex>
        <Box mb="8px">
          {account ? (
            doesMjtApprovePrediction ? (
              <Button
                width="100%"
                height="40px"
                disabled={disabled}
                className={disabled ? '' : 'swiper-no-swiping'}
                onClick={handleEnterPosition}
                isLoading={isTxPending}
                style={{ borderRadius: '30px' }}
                endIcon={isTxPending ? <AutoRenewIcon color="currentColor" spin /> : null}
              >
                {t(key, { symbol: payToken.symbol })}
              </Button>
            ) : (
              <Button
                width="100%"
                height="40px"
                className="swiper-no-swiping"
                onClick={handleApprove}
                isLoading={pendingTx}
                style={{ borderRadius: '30px' }}
                endIcon={pendingTx ? <AutoRenewIcon color="currentColor" spin /> : null}
              >
                {t('PREDICTION_50')}
              </Button>
            )
          ) : (
            <ConnectWalletButton className="swiper-no-swiping" width="100%" />
          )}
        </Box>
        <Text as="p" fontSize="12px" lineHeight={1} color="textRemark" textAlign="left" mt="10px">
          {t('PREDICTION_51')}
        </Text>
      </CardBody>
    </Card>
  )
}

export default SetPositionCard
